import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Home, DollarSign, Users, Target, User, Flag, Download, Share2, RefreshCw, Shield, Edit3, Camera } from "lucide-react";
import ShareCardModal from "@/components/ShareCardModal";
import ScoreEditModal from "@/components/ScoreEditModal";
import { useCourseData } from "@/hooks/useCourseData";
import { generateRoundPDF } from "@/utils/pdfGenerator";
import { VueltaSummaryCards } from "@/components/results/VueltaSummaryCards";
import { ScoresTab } from "@/components/results/ScoresTab";
import { MatchupsTab } from "@/components/results/MatchupsTab";
import { IndividualsTab } from "@/components/results/IndividualsTab";
import { OyesTab } from "@/components/results/OyesTab";
import { UnitsTab } from "@/components/results/UnitsTab";
import { UAReviewTab } from "@/components/results/UAReviewTab";
import { BalanceTab } from "@/components/results/BalanceTab";

export default function RoundResults() {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [showEditScores, setShowEditScores] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const fetchRound = useCallback(async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setRound(data);
        if (data.status !== "completed") {
          await fetch(`${API}/rounds/${roundId}/complete`, { method: "POST", credentials: "include" });
        }
      } else {
        toast.error("Ronda no encontrada");
        navigate("/dashboard");
      }
    } catch {
      
      toast.error("Error al cargar la ronda");
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate]);

  useEffect(() => { fetchRound(); }, [fetchRound]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await fetch(`${API}/rounds/${roundId}/recalculate`, { method: "POST", credentials: "include" });
      if (response.ok) {
        setRound(await response.json());
        toast.success("Resultados recalculados");
      } else {
        toast.error("Error al recalcular");
      }
    } catch {
      toast.error("Error al recalcular");
    } finally {
      setRecalculating(false);
    }
  };

  const { coursesData, coursesLoading } = useCourseData();

  if (loading || coursesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!round) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <p className="text-[#64748B]">Ronda no encontrada</p>
      </div>
    );
  }

  // Computed data
  const courseName = round?.course || "Guadalajara Country Club";
  const HOLES = coursesData?.[courseName] || [];
  const PAR_3_HOLES = HOLES.filter(h => h.par === 3).map(h => h.hole);
  const coursePar = HOLES.reduce((sum, h) => sum + h.par, 0);
  const outPar = HOLES.slice(0, 9).reduce((sum, h) => sum + h.par, 0);
  const inPar = HOLES.slice(9).reduce((sum, h) => sum + h.par, 0);
  const startingHole = round?.starting_hole || 1;
  const isVuelta1 = (holeNum) => startingHole === 1 ? holeNum <= 9 : holeNum >= 10;

  // Vuelta totals
  let v1BasePoints = 0, v1SecPoints = 0, v2BasePoints = 0, v2SecPoints = 0;
  let totalBasePoints = 0, totalSecondaryPoints = 0;
  round?.matchups?.forEach(m => {
    const v1 = m.vuelta1_points || 0;
    const v2 = m.vuelta2_points || 0;
    if (v1 > 0) v1BasePoints += v1; else v1SecPoints += Math.abs(v1);
    if (v2 > 0) v2BasePoints += v2; else v2SecPoints += Math.abs(v2);
    if (m.total_points > 0) totalBasePoints += m.total_points;
    else totalSecondaryPoints += Math.abs(m.total_points);
  });

  const totalUnits = {};
  round?.players?.forEach(player => {
    totalUnits[player.player_id] = player.scores?.reduce((sum, s) => sum + (s.unit_count || 0), 0) || 0;
  });

  const pointValue = round?.point_value || 0;
  const overallResult = totalBasePoints - totalSecondaryPoints;
  const baseWins = overallResult > 0;
  const tie = overallResult === 0;
  const basePlayers = round?.players?.filter(p => p.is_base) || [];

  // PDF generation
  const generatePDF = () => {
    return generateRoundPDF({
      round, basePlayers, HOLES, PAR_3_HOLES, outPar, inPar, coursePar,
      totalBasePoints, totalSecondaryPoints, v1BasePoints, v1SecPoints,
      v2BasePoints, v2SecPoints, tie, baseWins, overallResult, totalUnits, pointValue
    });
  };

  const handleExport = () => {
    setExporting(true);
    toast.info("Generando PDF...");
    try {
      const doc = generatePDF();
      doc.save(`${round.name.replace(/\s+/g, "_")}_${round.date}.pdf`);
      toast.success("PDF descargado");
    } catch {
      
      toast.error("Error al generar PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    if (!navigator.share) { handleExport(); return; }
    setExporting(true);
    toast.info("Preparando PDF...");
    try {
      const doc = generatePDF();
      const blob = doc.output("blob");
      const file = new File([blob], `${round.name}_resultados.pdf`, { type: "application/pdf" });
      await navigator.share({ title: `Resultados: ${round.name}`, text: `Resultados de la ronda de golf ${round.name} - ${round.date}`, files: [file] });
      toast.success("Compartido!");
    } catch (shareError) {
      if (shareError.name !== "AbortError") handleExport();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="gradient-hero text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="text-white hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Resultados</h1>
            <div className="w-10"></div>
          </div>
          <div className="text-center">
            <Trophy className="w-12 h-12 text-[#D4A373] mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="text-2xl font-bold mb-1">{round?.name}</h2>
            <p className="text-white/80 text-sm">{round?.date} - {round?.course}</p>
          </div>
          <div className="flex justify-center gap-3 mt-4">
            <Button onClick={handleRecalculate} disabled={recalculating} variant="secondary" size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="recalculate-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? "..." : "Refrescar"}
            </Button>
            <Button onClick={() => setShowEditScores(true)} variant="secondary" size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="edit-scores-btn">
              <Edit3 className="w-4 h-4 mr-2" /> Editar
            </Button>
            <Button onClick={() => setShowShareCard(true)} variant="secondary" size="sm" className="bg-[#D4A373] hover:bg-[#C4935F] text-white border-0" data-testid="share-card-btn">
              <Camera className="w-4 h-4 mr-2" /> Mi Tarjeta
            </Button>
            <Button onClick={handleShare} disabled={exporting} variant="secondary" size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-2" /> {exporting ? "..." : "Compartir"}
            </Button>
            <Button onClick={handleExport} disabled={exporting} variant="secondary" size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" /> {exporting ? "..." : "Descargar PDF"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 -mt-4">
        <VueltaSummaryCards
          v1BasePoints={v1BasePoints} v1SecPoints={v1SecPoints}
          v2BasePoints={v2BasePoints} v2SecPoints={v2SecPoints}
          totalBasePoints={totalBasePoints} totalSecondaryPoints={totalSecondaryPoints}
          baseWins={baseWins} tie={tie} overallResult={overallResult}
          hasDoubles={round?.matchups?.some(m => m.is_doubled)}
        />

        <Tabs defaultValue="scores" className="mb-8">
          <TabsList className={`grid w-full mb-4 ${round?.units_advantage_enabled ? 'grid-cols-7' : 'grid-cols-6'}`}>
            <TabsTrigger value="scores" data-testid="tab-scores"><User className="w-4 h-4 mr-1 hidden sm:block" />Scores</TabsTrigger>
            <TabsTrigger value="matchups" data-testid="tab-matchups"><Target className="w-4 h-4 mr-1 hidden sm:block" />Parejas</TabsTrigger>
            <TabsTrigger value="individuals" data-testid="tab-individuals"><Users className="w-4 h-4 mr-1 hidden sm:block" />Indiv.</TabsTrigger>
            <TabsTrigger value="oyes" data-testid="tab-oyes"><Flag className="w-4 h-4 mr-1 hidden sm:block" />Oyes</TabsTrigger>
            <TabsTrigger value="units" data-testid="tab-units"><Trophy className="w-4 h-4 mr-1 hidden sm:block" />Unidades</TabsTrigger>
            {round?.units_advantage_enabled && (
              <TabsTrigger value="ua" data-testid="tab-ua"><Shield className="w-4 h-4 mr-1 hidden sm:block" />U.Vent</TabsTrigger>
            )}
            <TabsTrigger value="balance" data-testid="tab-balance"><DollarSign className="w-4 h-4 mr-1 hidden sm:block" />Balance</TabsTrigger>
          </TabsList>

          <TabsContent value="scores">
            <ScoresTab round={round} HOLES={HOLES} outPar={outPar} inPar={inPar} coursePar={coursePar} />
          </TabsContent>

          <TabsContent value="matchups">
            <MatchupsTab round={round} PAR_3_HOLES={PAR_3_HOLES} isVuelta1={isVuelta1} />
          </TabsContent>

          <TabsContent value="individuals">
            <IndividualsTab round={round} isVuelta1={isVuelta1} />
          </TabsContent>

          <TabsContent value="oyes">
            <OyesTab round={round} PAR_3_HOLES={PAR_3_HOLES} />
          </TabsContent>

          <TabsContent value="units">
            <UnitsTab round={round} totalUnits={totalUnits} />
          </TabsContent>

          {round?.units_advantage_enabled && (
            <TabsContent value="ua">
              <UAReviewTab round={round} roundId={roundId} setRound={setRound} />
            </TabsContent>
          )}

          <TabsContent value="balance">
            <BalanceTab round={round} pointValue={pointValue} />
          </TabsContent>
        </Tabs>

        <Button onClick={() => navigate("/dashboard")} className="btn-primary w-full py-6" data-testid="back-to-dashboard-btn">
          <Home className="w-5 h-5 mr-2" /> Volver al Inicio
        </Button>
      </main>
      
      {showShareCard && <ShareCardModal round={round} onClose={() => setShowShareCard(false)} />}
      {showEditScores && (
        <ScoreEditModal round={round} onClose={() => setShowEditScores(false)} onSaved={() => { fetchRound(); toast.success("Scores actualizados y recalculados"); }} />
      )}
    </div>
  );
}
